import React, { useRef } from "react";
import { updateShortLink, deleteShortLink } from "actions/shortLink";
import { fetchAppCheckout } from "actions/app";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchAttachments } from "actions/attachment";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import {
  fetchMerchantAccountCouponsPromotionCodes,
  fetchMerchantAccountProductsPrices
} from "actions/merchantAccount";
import { validateUpdateShortLinkForm } from "utils/shortLink";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik } from "formik";
import Padder from "components/Padder";
import { getShortLinkDeleteModalSlides } from "utils/constants/modal";
import DetailsFields from "components/ShortLinkForm/Fields/Details";
import ProductsFields from "components/ShortLinkForm/Fields/Products";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import ReceiverFields from "components/ShortLinkForm/Fields/Receiver";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import ConfirmationFields from "components/ShortLinkForm/Fields/Confirmation";
import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import AttachmentsFields from "components/ShortLinkForm/Fields/Attachments";
import LinkPreviewFields from "components/ShortLinkForm/Fields/LinkPreview";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import {
  prepareEditShortLinkInitialValues,
  prepareUpdateShortLink
} from "utils/shortLink";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import { useCtxReferences } from "utils/selectors";
import isEmpty from "lodash/isEmpty";
import {
  ensureAppCheckout,
  ensureAttachments,
  ensureProductsPricesCouponsPromotionCodes,
  ensureShippingRates,
  ensureTaxRates
} from "utils/hooks";
import InfoNotice from "components/InfoNotice";
import ConfirmIconButton from "components/ConfirmIconButton";
import HoverIconButton from "components/HoverIconButton";
import FormActions from "components/FormActions";
import { PAYMENT_LINK_EDIT_RESTRICTION } from "utils/constants/notice";
import CheckoutDetails from "components/SaleForm/Fields/CheckoutDetails";
import { STATE_KEYS } from "utils/constants/state";
import compact from "lodash/compact";
import InfoList from "components/InfoList";
import { handleFormCb } from "utils/form";
import { THEMES } from "utils/constants/ui";
import { SHORT_LINK_NODE_KEY } from "utils/constants/shortLink";

const DEFAULT_CLASSES = {
  header: {
    container: "flex flex-column w-100 sticky bb hairline-1 bg-white top-0 z-1",
    titleWrapper: "flex flex-row justify-between items-center",
    title: "f4 fw5"
  },
  actions: {
    container:
      "flex flex-row items-center pv3 ph3 bb hairline-1 sticky top-0 z-5 bg-white",
    navigation: "flex w-100 items-center",
    titleWrapper: "flex flex-row items-center",
    title: "fw6 f3 word-break",
    form: "flex flex-shrink-0"
  },
  content: {
    container: "bg-white h-100"
  }
};

const ProductPaymentLinkForm = ({
  paymentLink,
  actions,
  customClasses,
  goBack
}) => {
  const classes = { ...DEFAULT_CLASSES, ...customClasses };
  const {
    products,
    prices,
    coupons
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });
  ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });

  const { taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates: actions.fetchMerchantAccountTaxRates
  });
  const { shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates: actions.fetchMerchantAccountShippingRates
  });

  ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });

  const buttonProps = null;
  const formRef = useRef(null);

  const { resourceUUID, resource } = useCtxReferences();

  const submitForm = (formValues, formikActions) => {
    const data = prepareUpdateShortLink(
      formRef.current.initialValues,
      formValues
    );

    if (!isEmpty(data)) {
      actions.updateShortLink(
        {
          resourceUUID,
          resource,
          uuid: paymentLink.uuid,
          data
        },
        (err) => handleFormCb(formValues, err, formikActions)
      );
    } else {
      console.warn("Short link: No updates to be made");
      formikActions.setSubmitting(false);
      formikActions.resetForm({ values: formValues });
    }
  };
  const deleteShortLink = (callback) => {
    actions.deleteShortLink(
      {
        resourceUUID,
        resource,
        uuid: paymentLink.uuid
      },
      (err, response) => {
        if (!err) {
          goBack();
        }
        callback(err, response);
      }
    );
  };

  const initialValues = prepareEditShortLinkInitialValues(paymentLink);
  const checkoutPreview = shortLinkCheckoutPreview({
    shortLink: paymentLink,
    products,
    prices,
    coupons,
    taxRates,
    shippingRates
  });

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateUpdateShortLinkForm({
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates
      })}
    >
      {({ values, errors }) => {
        const messagesList = errors &&
          errors.messages &&
          errors.messages.length > 0 && (
            <div className="w-100 dib">
              <InfoList
                header="Error"
                items={errors.messages}
                theme={THEMES.ERROR}
              />
            </div>
          );
        return (
          <div className="w-100">
            {/* Sticky action header */}
            <div className={classes.actions.container}>
              <div className={classes.actions.navigation}>
                {buttonProps && <HoverIconButton {...buttonProps} />}
                <div className={classes.actions.titleWrapper}>
                  <div className={classes.actions.title}>{values.title}</div>
                </div>
              </div>
              <div className={classes.actions.form}>
                <FormActions theme="slim">
                  <div className="pr2">
                    <ConfirmIconButton
                      slides={getShortLinkDeleteModalSlides(values)}
                      icon="delete"
                      onConfirm={deleteShortLink}
                    />
                  </div>
                </FormActions>
              </div>
            </div>
            <div className="pa3">
              {compact([
                messagesList
                  ? {
                      key: SHORT_LINK_NODE_KEY.MESSAGES,
                      node: messagesList
                    }
                  : null,
                {
                  key: SHORT_LINK_NODE_KEY.INFO,
                  node: <InfoNotice copy={PAYMENT_LINK_EDIT_RESTRICTION} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.DETAILS,
                  node: <DetailsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.PREVIEW,
                  node: <LinkPreviewFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.PRODUCTS,
                  node: <ProductsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.ATTACHMENTS,
                  node: <AttachmentsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.PAYMENT,
                  node: <PaymentFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.TAX,
                  node: <TaxRatesFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.COUPONS,
                  node: <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.RECEIVER,
                  node: <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.SHIPPING,
                  node: <ShippingFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.RECEIVER,
                  node: <RedirectsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.CONFIRMATION,
                  node: (
                    <ConfirmationFields
                      name={STATE_KEYS.SHORT_LINK.CONFIRMATION_DATA}
                    />
                  )
                },
                {
                  key: SHORT_LINK_NODE_KEY.CHECKOUT_PREVIEW,
                  node: (
                    <CheckoutDetails
                      header="Checkout preview"
                      collapseImages={checkoutPreview.collapseImages}
                      {...checkoutPreview.checkout}
                    />
                  )
                }
              ]).map(({ node, key }, nodeIx) => (
                <div className={nodeIx ? "pt3 pt4-ns" : ""} key={key}>
                  {node}
                </div>
              ))}
            </div>
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateShortLink,
      deleteShortLink,
      fetchAppCheckout,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ProductPaymentLinkForm);
